import { ref, computed, watch } from 'vue'
import { getCookie, setCookie, removeCookie } from '../utils/cookies'
import { createAxiosInstance } from '../utils/axios.js'

const api = createAxiosInstance()

// ==================== REACTIVE STATE ====================
const cartItems = ref([])
const orderDiscountCode = ref(null)
const cartTotals = ref({
  subtotal: 0,
  discount_amount: 0,
  total: 0,
  items: [],
  discount_code: null,
  discount_description: null
})
const isLoading = ref(false)
const isSyncing = ref(false)

// ==================== SERVER SYNC STATE ====================
let currentUserId = null
let serverSyncTimer = null
let serverSyncInFlight = false
let serverSyncDirty = false

// Cross-device sync: snapshot of the cart as last seen on the server, plus a
// visibility-aware poll that reconciles changes made on other devices.
let lastServerCart = null
let serverCartPollTimer = null
const SERVER_CART_POLL_MS = 10000

// Deep copy so the "last seen on server" snapshot can never be mutated by the
// caller/mock that holds the same reference (matches the server's JSON round-trip).
const cloneCart = cart => JSON.parse(JSON.stringify(cart))

// ==================== STORAGE CONSTANTS ====================
const CART_COOKIE_KEY = 'barry_bonna_cart'
const CART_STORAGE_KEY = 'barry_bonna_cart'

// ==================== COMPUTED PROPERTIES ====================
const subtotal = computed(() => cartTotals.value.subtotal)
const discountAmount = computed(() => cartTotals.value.discount_amount)
const total = computed(() => cartTotals.value.total)
const itemCount = computed(() => {
  return cartItems.value
    .filter(item => !item.isUnavailable)
    .reduce((sum, item) => sum + item.quantity, 0)
})

// ==================== STORAGE FUNCTIONS ====================
const buildCartData = () => ({
  items: cartItems.value.map(item => ({
    id: item.id,
    bookId: item.bookId,
    bookFormatId: item.bookFormatId,
    eventId: item.eventId,
    format: item.format,
    quantity: item.quantity,
    ticketNames: item.ticketNames
  })),
  orderDiscountCode: orderDiscountCode.value
})

const saveCart = () => {
  try {
    // Stamp the stored copy with whose cart it is. Without an owner we cannot
    // tell a guest cart (whose items should be ADDED to the account on sign-in)
    // from a cache of the signed-in user's own cart (which must never be added
    // to itself) — that ambiguity is what inflated quantities on every page
    // load. The server payload stays unstamped; this field is local only.
    const cartData = { ...buildCartData(), ownerId: currentUserId }
    const encoded = encodeURIComponent(JSON.stringify(cartData))

    if (encoded.length > 4000) {
      localStorage.setItem(CART_STORAGE_KEY, encoded)
      removeCookie(CART_COOKIE_KEY)
    } else {
      setCookie(CART_COOKIE_KEY, encoded, 365)
      localStorage.removeItem(CART_STORAGE_KEY)
    }
  } catch (error) {
    console.error('Failed to save cart:', error)
  }
}

const loadCartFromStorage = () => {
  try {
    let stored = getCookie(CART_COOKIE_KEY)
    if (!stored) {
      stored = localStorage.getItem(CART_STORAGE_KEY)
    }

    if (stored) {
      const parsed = JSON.parse(decodeURIComponent(stored))
      return {
        items: parsed.items || [],
        orderDiscountCode: parsed.orderDiscountCode || null,
        ownerId: parsed.ownerId != null ? parsed.ownerId : null,
        // Carts written before the owner stamp existed. `ownerId: null` means
        // "definitely a guest cart"; a missing field means "unknown", and the
        // two must not be treated alike.
        hasOwner: Object.prototype.hasOwnProperty.call(parsed, 'ownerId')
      }
    }
  } catch (error) {
    console.error('Failed to load cart from storage:', error)
  }
  return { items: [], orderDiscountCode: null, ownerId: null, hasOwner: true }
}

// ==================== SERVER CART SYNC ====================
const setUserId = (userId = null) => {
  currentUserId = userId || null
  if (currentUserId) {
    lastServerCart = null
    scheduleServerCartPoll()
  } else {
    stopServerCartWatch()
  }
}

const loadCartFromServer = async (userId = null) => {
  if (!userId) return null
  try {
    const response = await api.get('/api/user/cart')
    const cart = response.data.cart
    if (cart && Array.isArray(cart.items)) {
      return {
        items: cart.items,
        orderDiscountCode: cart.orderDiscountCode || null
      }
    }
  } catch (error) {
    console.error('Failed to load cart from server:', error)
  }
  return null
}

const itemKey = item => {
  if (item.bookId) return `book:${item.bookId}:${item.format || ''}`
  if (item.eventId) return `event:${item.eventId}`
  return item.id || `item:${JSON.stringify(item)}`
}

// Merge a guest/local cart with the server cart. Server items are the compact
// storage shape; local items carry richer display data. When keys collide the
// local (richer) item is kept and quantities are summed (digital capped at 1).
const mergeCarts = (local, server) => {
  const merged = new Map()

  const add = item => {
    const key = itemKey(item)
    const existing = merged.get(key)
    if (existing) {
      if (item.quantity !== undefined) {
        existing.quantity = isDigitalFormat(existing.format)
          ? 1
          : existing.quantity + item.quantity
      }
      if (item.ticketNames && item.ticketNames.length > 0) {
        existing.ticketNames = existing.ticketNames || []
        existing.ticketNames.push(...item.ticketNames)
      }
    } else {
      merged.set(key, { ...item })
    }
  }

  ;(local.items || []).forEach(add)
  ;(server.items || []).forEach(add)

  return {
    items: Array.from(merged.values()),
    orderDiscountCode: local.orderDiscountCode || server.orderDiscountCode || null
  }
}

// Take the server cart as authoritative, keeping any local item the server has
// not seen yet (something added a moment ago whose debounced push is still in
// flight). Quantities are ADOPTED, never added — this is the same cart coming
// back to us, not a second one.
const adoptServerCart = (local, server) => {
  const adopted = new Map()
  ;(server.items || []).forEach(item => adopted.set(itemKey(item), { ...item }))
  ;(local.items || []).forEach(item => {
    const key = itemKey(item)
    if (!adopted.has(key)) adopted.set(key, { ...item })
  })
  return {
    items: Array.from(adopted.values()),
    orderDiscountCode: server.orderDiscountCode || local.orderDiscountCode || null
  }
}

// How the stored cart and the server cart combine on a PAGE LOAD.
//
// Never sums, whatever the stamp says. Adding a cart to itself is what made
// quantities climb on every navigation, and a page load is never the moment a
// guest cart legitimately joins an account — that is sign-in, below. Keeping
// this path unconditionally non-summing also means a cart mis-stamped as a
// guest (currentUserId can be null if the Navbar calls loadCart() before the
// stored user object is readable) can no longer cause a doubling.
const resolveCartOnLoad = (stored, server, userId) => {
  const belongsToSomeoneElse =
    stored.hasOwner && stored.ownerId !== null && stored.ownerId !== userId
  if (belongsToSomeoneElse) {
    // A different account's leftovers in this browser: never inherit them.
    return { items: [...(server.items || [])], orderDiscountCode: server.orderDiscountCode || null }
  }

  // Our own cache, or a pre-stamp cart we cannot classify. Adopting keeps
  // unsynced local additions without ever summing.
  return adoptServerCart(stored, server)
}

// How they combine at SIGN-IN, the one moment a guest cart is a genuinely
// separate cart whose items should be added to the account.
const resolveCartOnSignIn = (stored, server, userId) => {
  const isGuestCart = stored.hasOwner && stored.ownerId === null
  if (isGuestCart) return mergeCarts(stored, server)
  return resolveCartOnLoad(stored, server, userId)
}

const flushCartToServer = async (userId) => {
  if (serverSyncInFlight || serverSyncDirty) {
    serverSyncDirty = true
    return
  }
  serverSyncInFlight = true
  try {
    const snapshot = buildCartData()
    await api.put('/api/user/cart', { cart: snapshot })
    lastServerCart = cloneCart(snapshot)
  } catch (error) {
    console.error('Failed to sync cart to server:', error)
  } finally {
    serverSyncInFlight = false
    if (serverSyncDirty) {
      serverSyncDirty = false
      await flushCartToServer(userId)
    }
  }
}

const pushCartToServer = (userId = currentUserId, { flush = false } = {}) => {
  if (!userId) return
  if (serverSyncTimer) {
    clearTimeout(serverSyncTimer)
    serverSyncTimer = null
  }
  if (flush) {
    serverSyncDirty = false
    flushCartToServer(userId)
  } else {
    serverSyncTimer = setTimeout(() => {
      serverSyncTimer = null
      serverSyncDirty = false
      flushCartToServer(userId)
    }, 500)
  }
}

// Pull the server cart, merge in the guest cart, push the merged result back and
// keep the server as the single source of truth. Called on login/register.
const mergeAndSync = async (userId) => {
  if (!userId) return
  currentUserId = userId
  const local = loadCartFromStorage()
  const serverCart = await loadCartFromServer(userId)

  if (serverCart) {
    const resolved = resolveCartOnSignIn(local, serverCart, userId)
    cartItems.value = resolved.items
    orderDiscountCode.value = resolved.orderDiscountCode
    lastServerCart = cloneCart(serverCart)
    pushCartToServer(userId, { flush: true })
  }

  await syncCartWithBackend(userId)
  await calculateCartTotals(userId)
  scheduleServerCartPoll()
}

// ==================== CROSS-DEVICE SYNC (poll + reconcile) ====================
// Other devices (another browser, phone, desktop) write their own copies of the
// signed-in user's cart to /api/user/cart. This device polls that cart while
// signed in and applies a 3-way reconcile so a removal (or add / quantity
// change) on another device shows up here within ~10s — WITHOUT clobbering an
// edit made locally after this device's last sync (last-write-wins).

// Compact cart shape, same as buildCartData() / the server's GET response:
// { items: [...], orderDiscountCode }. Returns true if a remote change applied.
const reconcileServerCart = (serverCart) => {
  const base = lastServerCart || { items: [], orderDiscountCode: null }
  const baseMap = new Map(base.items.map(i => [itemKey(i), i]))
  const serverMap = new Map(serverCart.items.map(i => [itemKey(i), i]))
  const localMap = new Map(cartItems.value.map(i => [itemKey(i), i]))

  const quantityOf = i => (i && i.quantity !== undefined ? i.quantity : 1)
  const ticketsOf = i => (i && Array.isArray(i.ticketNames) ? i.ticketNames : [])
  const ticketsEqual = (a, b) => a.length === b.length && a.every((n, idx) => n === b[idx])

  const isLocallyEdited = (localItem, baseItem) => {
    if (!localItem) return false
    if (!baseItem) return true
    if (quantityOf(localItem) !== quantityOf(baseItem)) return true
    return !ticketsEqual(ticketsOf(localItem), ticketsOf(baseItem))
  }

  const next = new Map()
  let changed = false

  // Items the server currently holds.
  serverMap.forEach((serverItem, key) => {
    const localItem = localMap.get(key)
    const baseItem = baseMap.get(key)

    if (localItem && isLocallyEdited(localItem, baseItem)) {
      // We edited this after our last sync: keep our version; the pending push
      // flushes it back up (last-write-wins).
      next.set(key, { ...localItem })
    } else if (localItem) {
      // Unchanged locally since our last sync: adopt the remote state.
      const merged = { ...localItem }
      if (quantityOf(merged) !== quantityOf(serverItem)) {
        merged.quantity = quantityOf(serverItem)
        changed = true
      }
      if (serverItem.ticketNames && !ticketsEqual(ticketsOf(merged), serverItem.ticketNames)) {
        merged.ticketNames = [...serverItem.ticketNames]
        changed = true
      }
      next.set(key, merged)
    } else {
      // New item added on another device (compact shape; syncCartWithBackend
      // rehydrates the display data afterwards).
      next.set(key, { ...serverItem, isUnavailable: false })
      changed = true
    }
  })

  // Items we hold that the server no longer has.
  localMap.forEach((localItem, key) => {
    if (next.has(key) || serverMap.has(key)) return
    if (baseMap.has(key)) {
      if (isLocallyEdited(localItem, baseMap.get(key))) {
        // Edited locally since our last sync and not yet flushed: keep it — the
        // pending push re-adds it (last-write-wins over the remote removal).
        next.set(key, { ...localItem })
      } else {
        // Was on our last-known server cart, unchanged locally, now gone on the
        // server: a remote device removed it.
        changed = true
      }
    } else {
      // Added locally after our last sync, not yet on the server: keep it.
      next.set(key, { ...localItem })
    }
  })

  cartItems.value = Array.from(next.values())

  // Discount code: same last-write-wins rule.
  const baseDiscount = base.orderDiscountCode || null
  const serverDiscount = serverCart.orderDiscountCode || null
  if (orderDiscountCode.value !== serverDiscount && orderDiscountCode.value === baseDiscount) {
    orderDiscountCode.value = serverDiscount
    changed = true
  }

  lastServerCart = cloneCart(serverCart)
  return changed
}

const pollServerCart = async () => {
  if (!currentUserId || document.hidden || isLoading.value || isSyncing.value) return
  const serverCart = await loadCartFromServer(currentUserId)
  if (!serverCart) return
  if (reconcileServerCart(serverCart)) {
    await syncCartWithBackend(currentUserId)
    await calculateCartTotals(currentUserId)
  }
}

const scheduleServerCartPoll = () => {
  if (serverCartPollTimer) {
    clearTimeout(serverCartPollTimer)
    serverCartPollTimer = null
  }
  if (!currentUserId || document.hidden) return
  serverCartPollTimer = setTimeout(async () => {
    serverCartPollTimer = null
    try {
      await pollServerCart()
    } catch (error) {
      // A failed poll must never break the loop or the site — log and reschedule.
      console.error('Cross-device cart sync poll failed:', error)
    } finally {
      scheduleServerCartPoll()
    }
  }, SERVER_CART_POLL_MS)
}

const stopServerCartWatch = () => {
  if (serverCartPollTimer) {
    clearTimeout(serverCartPollTimer)
    serverCartPollTimer = null
  }
}

// ==================== SYNC FUNCTION ====================
const syncCartWithBackend = async (userId = null) => {
  if (isSyncing.value) return { removedItems: [], discountCleared: false }

  isSyncing.value = true
  try {
    // Prepare items for sync
    const syncItems = cartItems.value.map(item => ({
      book_id: item.bookId || null,
      book_format_id: item.bookFormatId || null,
      event_id: item.eventId || null,
      format: item.format || null
    }))

    if (syncItems.length === 0 && !orderDiscountCode.value) {
      return { removedItems: [], discountCleared: false }
    }

    // Fetch fresh data from backend
    const response = await api.post('/api/discounts/sync-items', {
      items: syncItems,
      user_id: userId || null,
      discount_code: orderDiscountCode.value || null
    })

    const { books, events, discount_valid, discount_message } = response.data

    // Create lookup maps by composite keys
    const bookLookup = new Map()
    books.forEach(book => {
      const key = `${book.id}-${book.format}`
      bookLookup.set(key, book)
    })

    const eventLookup = new Map()
    events.forEach(event => {
      eventLookup.set(event.id, event)
    })

    const updatedItems = []
    const removedItems = []
    let hasChanges = false

    // Sync each cart item with backend data
    for (const item of cartItems.value) {
      if (item.bookId) {
        const lookupKey = `${item.bookId}-${item.format}`
        const syncedBook = bookLookup.get(lookupKey)

        if (syncedBook) {
          const updated = {
            id: item.id,
            bookId: syncedBook.id,
            bookFormatId: syncedBook.book_format_id,
            title: syncedBook.title,
            author: syncedBook.author,
            coverImage: syncedBook.cover_image,
            price: syncedBook.price,
            format: syncedBook.format,
            discountPercentage: syncedBook.discount_percentage,
            quantity: item.quantity,
            imageCropDimensions: item.imageCropDimensions,
            isUnavailable: false
          }

          // Check if anything changed
          if (
            item.price !== updated.price ||
            item.discountPercentage !== updated.discountPercentage ||
            item.bookFormatId !== updated.bookFormatId
          ) {
            hasChanges = true
          }

          updatedItems.push(updated)
        } else {
          // Item not found in backend - remove it
          hasChanges = true
          removedItems.push({
            id: item.id,
            title: item.title,
            type: 'book'
          })
        }
      } else if (item.eventId) {
        const syncedEvent = eventLookup.get(item.eventId)

        if (syncedEvent) {
          const updated = {
            id: item.id,
            eventId: syncedEvent.id,
            title: syncedEvent.title,
            date: syncedEvent.date,
            location: syncedEvent.location,
            image: syncedEvent.image,
            price: syncedEvent.ticket_price || 0,
            quantity: item.quantity,
            ticketNames: item.ticketNames || [],
            isUnavailable: false
          }

          if (item.price !== updated.price) {
            hasChanges = true
          }

          updatedItems.push(updated)
        } else {
          hasChanges = true
          removedItems.push({
            id: item.id,
            title: item.title,
            type: 'event'
          })
        }
      }
    }

    // Update cart items
    cartItems.value = updatedItems

    // Handle discount validation
    let discountCleared = false
    if (discount_valid === false && orderDiscountCode.value) {
      orderDiscountCode.value = null
      discountCleared = true
      hasChanges = true
    }

    // Save if changes occurred
    if (hasChanges) {
      saveCart()
    }

    return {
      removedItems,
      discountCleared,
      discountMessage: discount_message,
      hasChanges
    }
  } catch (error) {
    console.error('Failed to sync cart with backend:', error)
    return { removedItems: [], discountCleared: false, hasChanges: false }
  } finally {
    isSyncing.value = false
  }
}

// ==================== CALCULATE TOTALS ====================
const calculateCartTotals = async (userId = null) => {
  const availableItems = cartItems.value.filter(item => !item.isUnavailable)

  if (availableItems.length === 0) {
    cartTotals.value = {
      subtotal: 0,
      discount_amount: 0,
      total: 0,
      items: [],
      discount_code: null,
      discount_description: null
    }
    return
  }

  try {
    const items = availableItems.map(item => ({
      book_id: item.bookId || null,
      book_format_id: item.bookFormatId || null,
      event_id: item.eventId || null,
      format: item.format || null,
      quantity: item.quantity || 1
    }))

    const response = await api.post('/api/discounts/calculate-totals', {
      items,
      order_discount_code: orderDiscountCode.value,
      user_id: userId
    })

    cartTotals.value = response.data

    // If discount was rejected by backend, clear it
    if (orderDiscountCode.value && !response.data.discount_code) {
      orderDiscountCode.value = null
      saveCart()
    }
  } catch (error) {
    console.error('Failed to calculate cart totals:', error)
    // Fallback calculation
    const fallbackSubtotal = availableItems.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    )
    cartTotals.value = {
      subtotal: fallbackSubtotal,
      discount_amount: 0,
      total: fallbackSubtotal,
      items: availableItems.map(item => ({
        book_id: item.bookId,
        book_format_id: item.bookFormatId,
        event_id: item.eventId,
        name: item.title,
        quantity: item.quantity,
        unit_price: item.price,
        item_total: item.price * item.quantity
      })),
      discount_code: null,
      discount_description: null
    }
  }
}

// ==================== CART OPERATIONS ====================

// E-books and audiobooks are digital: a customer can only ever own one copy, so
// their cart quantity is always capped at 1 (the digital grant is deduped
// server-side, so buying "2" would charge twice but deliver one).
const isDigitalFormat = (format) =>
  ['ebook', 'audiobook'].includes(String(format || '').toLowerCase())

const addToCart = async (book, format, userId = null) => {
  const formatType = format.formatType || format.type || format
  const formatId = format.id || null

  let formatPrice = 0
  if (format.price) {
    formatPrice =
      typeof format.price === 'string'
        ? parseFloat(format.price.replace(/[^0-9.]/g, ''))
        : format.price
  } else if (book.price) {
    formatPrice =
      typeof book.price === 'string'
        ? parseFloat(book.price.replace(/[^0-9.]/g, ''))
        : book.price
  }

  // Check if item already exists
  const existingItem = cartItems.value.find(
    item => item.bookId === book.id && item.format === formatType
  )

  if (existingItem) {
    // Digital items stay at a single copy; only physical formats accumulate.
    if (!isDigitalFormat(formatType)) {
      existingItem.quantity += 1
    }
  } else {
    cartItems.value.push({
      id: `${book.id}-${formatType}-${Date.now()}`,
      bookId: book.id,
      bookFormatId: formatId,
      title: book.title,
      author: book.author,
      coverImage: book.coverImage,
      imageCropDimensions: book.imageCropDimensions || null,
      format: formatType,
      price: formatPrice,
      quantity: 1,
      isUnavailable: false
    })
  }

  saveCart()
  await syncCartWithBackend(userId)
  await calculateCartTotals(userId)
}

const addEventToCart = async (
  event,
  quantity = 1,
  ticketNames = [],
  userId = null
) => {
  const ticketPrice =
    typeof event.ticketPrice === 'string'
      ? parseFloat(event.ticketPrice.replace(/[^0-9.]/g, ''))
      : event.ticketPrice || 0

  const existingItem = cartItems.value.find(item => item.eventId === event.id)

  if (existingItem) {
    existingItem.quantity += quantity
    if (ticketNames && ticketNames.length > 0) {
      existingItem.ticketNames = existingItem.ticketNames || []
      existingItem.ticketNames.push(...ticketNames)
    }
  } else {
    cartItems.value.push({
      id: `event-${event.id}-${Date.now()}`,
      eventId: event.id,
      title: event.title,
      date: event.date,
      location: event.location,
      image: event.image,
      price: ticketPrice,
      quantity: quantity,
      ticketNames:
        ticketNames && ticketNames.length > 0 ? [...ticketNames] : [],
      isUnavailable: false
    })
  }

  saveCart()
  await calculateCartTotals(userId)
}

const removeFromCart = async (itemId, userId = null) => {
  cartItems.value = cartItems.value.filter(item => item.id !== itemId)
  saveCart()
  await calculateCartTotals(userId)
}

const updateQuantity = async (itemId, newQuantity, userId = null) => {
  const item = cartItems.value.find(item => item.id === itemId)
  if (item) {
    if (newQuantity <= 0) {
      await removeFromCart(itemId, userId)
    } else {
      // Digital items can never exceed a single copy, whatever the caller asks.
      item.quantity = isDigitalFormat(item.format) ? 1 : newQuantity
      saveCart()
      await calculateCartTotals(userId)
    }
  }
}

const incrementQuantity = async (itemId, userId = null) => {
  const item = cartItems.value.find(item => item.id === itemId)
  if (item) {
    await updateQuantity(itemId, item.quantity + 1, userId)
  }
}

const decrementQuantity = async (itemId, userId = null) => {
  const item = cartItems.value.find(item => item.id === itemId)
  if (item) {
    await updateQuantity(itemId, item.quantity - 1, userId)
  }
}

// ==================== DISCOUNT OPERATIONS ====================
const applyOrderDiscountCode = async (code, userId = null) => {
  if (!code || !code.trim()) {
    orderDiscountCode.value = null
    saveCart()
    await calculateCartTotals(userId)
    return { success: false, message: 'Discount code cannot be empty' }
  }

  try {
    const subtotal = cartItems.value.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    )

    const response = await api.post('/api/discounts/validate', {
      code: code.trim(),
      item_price: subtotal,
      user_id: userId || null
    })

    if (response.data.valid) {
      orderDiscountCode.value = response.data.discount.code
      saveCart()
      await calculateCartTotals(userId)
      return { success: true, message: 'Discount applied successfully' }
    } else {
      orderDiscountCode.value = null
      saveCart()
      await calculateCartTotals(userId)
      return {
        success: false,
        message: response.data.message || 'Invalid discount code'
      }
    }
  } catch (error) {
    console.error('Failed to validate discount code:', error)
    orderDiscountCode.value = null
    saveCart()
    await calculateCartTotals(userId)
    return {
      success: false,
      message:
        error.response?.data?.message || 'Failed to validate discount code'
    }
  }
}

const clearOrderDiscount = async (userId = null) => {
  orderDiscountCode.value = null
  saveCart()
  await calculateCartTotals(userId)
}

const validateStoredDiscount = async (userId = null) => {
  if (!orderDiscountCode.value) {
    return { cleared: false }
  }

  try {
    const subtotal = cartItems.value.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    )
    const response = await api.post('/api/discounts/validate', {
      code: orderDiscountCode.value.trim(),
      item_price: subtotal,
      user_id: userId
    })

    if (!response.data.valid) {
      const clearedCode = orderDiscountCode.value
      orderDiscountCode.value = null
      saveCart()
      await calculateCartTotals(userId)
      return {
        cleared: true,
        message: `Discount code ${clearedCode} was removed as it has already been used or is no longer valid.`
      }
    }
  } catch (error) {
    console.error('Failed to validate stored discount:', error)
    orderDiscountCode.value = null
    saveCart()
    await calculateCartTotals(userId)
    return {
      cleared: true,
      message: `Discount code was removed as it is no longer valid.`
    }
  }
  return { cleared: false }
}

// ==================== LOAD CART ====================
// Navbar, CartPage and CheckoutPage all mount on the same page load and each
// call loadCart during the same tick. Concurrent callers must share ONE
// in-flight load — returning early with an empty cart (the old isLoading
// guard) bounced /checkout back to /cart for signed-in users, whose cartItems
// only populate once the server GET resolves.
let loadCartInFlight = null

const loadCart = (userId = null) => {
  if (loadCartInFlight) return loadCartInFlight
  loadCartInFlight = performLoadCart(userId).finally(() => {
    loadCartInFlight = null
  })
  return loadCartInFlight
}

const performLoadCart = async (userId = null) => {
  if (isLoading.value) return { removedItems: [] }

  isLoading.value = true
  try {
    currentUserId = userId || null

    // Load from storage (guest cart / offline cache)
    const stored = loadCartFromStorage()

    // Authenticated users merge in the server cart; the server stays the
    // source of truth and the merged result is pushed straight back.
    if (userId) {
      const serverCart = await loadCartFromServer(userId)
      if (serverCart) {
        const resolved = resolveCartOnLoad(stored, serverCart, userId)
        cartItems.value = resolved.items
        orderDiscountCode.value = resolved.orderDiscountCode
        lastServerCart = cloneCart(serverCart)
        pushCartToServer(userId, { flush: true })
      } else {
        // No cart came back. Keep our own, but still refuse to inherit one that
        // another account left behind in this browser.
        const resolved = resolveCartOnLoad(
          stored,
          { items: [], orderDiscountCode: null },
          userId
        )
        cartItems.value = resolved.items
        orderDiscountCode.value = resolved.orderDiscountCode
        if (cartItems.value.length || orderDiscountCode.value) {
          pushCartToServer(userId, { flush: true })
        }
      }
    } else {
      cartItems.value = stored.items
      orderDiscountCode.value = stored.orderDiscountCode
    }

    // Sync with backend to get fresh prices and validate items
    const syncResult = await syncCartWithBackend(userId)

    // Calculate totals
    await calculateCartTotals(userId)

    // Watch for changes made on other devices while signed in.
    scheduleServerCartPoll()

    return syncResult
  } catch (error) {
    console.error('Failed to load cart:', error)
    cartItems.value = []
    orderDiscountCode.value = null
    cartTotals.value = {
      subtotal: 0,
      discount_amount: 0,
      total: 0,
      items: [],
      discount_code: null,
      discount_description: null
    }
    return { removedItems: [] }
  } finally {
    isLoading.value = false
  }
}

// ==================== HELPER FUNCTIONS ====================
const getItemTotal = item => {
  const backendItem = cartTotals.value.items.find(backendItem => {
    if (item.bookId) {
      if (backendItem.book_id !== item.bookId) return false

      // Match by book_format_id if available
      if (
        item.bookFormatId !== null &&
        item.bookFormatId !== undefined &&
        backendItem.book_format_id !== null &&
        backendItem.book_format_id !== undefined
      ) {
        return backendItem.book_format_id === item.bookFormatId
      }

      // Match by format name in item name
      if (item.format && backendItem.name) {
        return backendItem.name.includes(`(${item.format})`)
      }

      return !item.format && !backendItem.name.includes('(')
    }
    if (item.eventId) {
      return backendItem.event_id === item.eventId
    }
    return false
  })

  return backendItem ? backendItem.item_total : item.price * item.quantity
}

const clearCart = async (userId = null) => {
  cartItems.value = []
  orderDiscountCode.value = null
  cartTotals.value = {
    subtotal: 0,
    discount_amount: 0,
    total: 0,
    items: [],
    discount_code: null,
    discount_description: null
  }
  removeCookie(CART_COOKIE_KEY)
  localStorage.removeItem(CART_STORAGE_KEY)
  const targetUserId = userId || currentUserId
  if (targetUserId) {
    pushCartToServer(targetUserId, { flush: true })
  }
}

// Clear the local (guest) cart without touching the server cart. Used on
// logout so the account's items don't linger for the next visitor, while the
// user's persisted cart stays intact for their next login. The caller must
// setUserId(null) first so the cart watcher never flushes an empty cart up.
const clearLocalCart = () => {
  cartItems.value = []
  orderDiscountCode.value = null
  cartTotals.value = {
    subtotal: 0,
    discount_amount: 0,
    total: 0,
    items: [],
    discount_code: null,
    discount_description: null
  }
  removeCookie(CART_COOKIE_KEY)
  localStorage.removeItem(CART_STORAGE_KEY)
}

// ==================== WATCHERS ====================
watch(
  [cartItems, orderDiscountCode],
  () => {
    saveCart()
    pushCartToServer()
  },
  { deep: true }
)

// Coming back to the tab (or opening it) should catch up immediately rather
// than waiting for the next poll tick.
document.addEventListener('visibilitychange', () => {
  if (!currentUserId) return
  if (document.hidden) {
    stopServerCartWatch()
  } else {
    scheduleServerCartPoll()
    pollServerCart().catch(error => console.error('Cross-device cart sync poll failed:', error))
  }
})

// ==================== EXPORTS ====================
export const cartService = {
  // State
  cartItems,
  orderDiscountCode,
  cartTotals,
  isLoading,
  isSyncing,

  // Computed
  subtotal,
  discountAmount,
  total,
  itemCount,

  // Cart operations
  addToCart,
  addEventToCart,
  removeFromCart,
  updateQuantity,
  incrementQuantity,
  decrementQuantity,

  // Discount operations
  applyOrderDiscountCode,
  clearOrderDiscount,
  validateStoredDiscount,

  // Sync and load
  loadCart,
  syncCartWithBackend,
  syncCartItemsWithDB: syncCartWithBackend, // Backward compatibility alias
  calculateCartTotals,

  // Server cart sync
  setUserId,
  pushCartToServer,
  loadCartFromServer,
  mergeAndSync,
  mergeCarts,

  // Cross-device sync
  reconcileServerCart,
  pollServerCart,
  startServerCartWatch: scheduleServerCartPoll,
  stopServerCartWatch,

  // Helpers
  getItemTotal,
  clearCart,
  clearLocalCart
}
